import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";

import AnnouncementDetailsPage from "../components/AnnouncementDetailsPage";
import AnnouncementDetailsViewPage from "../components/AnnouncementDetailsViewPage";
import { TypedAnnouncementDelete, TypedAnnouncementUpdate } from "../mutations";
import { useAnnouncementDetailsQuery } from "../queries";
import { AnnouncementDelete } from "../types/AnnouncementDelete";
import { AnnouncementUpdate } from "../types/AnnouncementUpdate";
import {
  announcementListUrl,
  announcementUrl,
  AnnouncementUrlDialog,
  AnnouncementUrlQueryParams
} from "../urls";

interface AnnouncementDetailsProps {
  id: string;
  params: AnnouncementUrlQueryParams;
}

export const AnnouncementDetails: React.FC<AnnouncementDetailsProps> = props => {
  const { id, params } = props;
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const user = useUser();

  const { data, loading } = useAnnouncementDetailsQuery({
    displayLoader: true,
    variables: {
      id
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    AnnouncementUrlDialog,
    AnnouncementUrlQueryParams
  >(navigate, params => announcementUrl(id, params), params);

  const handleAnnouncementUpdate = (data: AnnouncementUpdate) => {
    if (data.announcementUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully updated announcement"
        })
      });
      navigate(announcementUrl(data.announcementUpdate.announcement.id), true);
    } else {
      notify({
        status: "error",
        text: data.announcementUpdate.errors[0].message
      });
    }
  };

  const handleAnnouncementDelete = (data: AnnouncementDelete) => {
    if (data.announcementDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully deleted announcement"
        })
      });
      navigate(announcementListUrl());
    } else {
      notify({
        status: "error",
        text: data.announcementDelete.errors[0].message
      });
    }
  };

  if (
    user.user.userType.code === "" ||
    user.user.userType.code === SmopUserTypeCode.MARKETING
  ) {
    return (
      <TypedAnnouncementDelete onCompleted={handleAnnouncementDelete}>
        {(announcementDelete, announcementDeleteOpts) => (
          <TypedAnnouncementUpdate onCompleted={handleAnnouncementUpdate}>
            {(AnnouncementUpdate, AnnouncementOpts) => (
              <>
                <WindowTitle
                  title={intl.formatMessage(sectionNames.announcements)}
                />
                <AnnouncementDetailsPage
                  errors={
                    AnnouncementOpts.data?.announcementUpdate.errors || []
                  }
                  data={maybe(() => data.announcement)}
                  onBack={() => navigate(announcementListUrl())}
                  onDelete={() => openModal("delete")}
                  disabled={AnnouncementOpts.loading || loading}
                  saveButtonBarState={AnnouncementOpts.status}
                  onSubmit={(formData, file) => {
                    const withImageParams = {
                      content: JSON.stringify(formData.content),
                      endDate: `${formData.endDate}T00:00:00+00:00`,
                      ...(file && {
                        image: file
                      }),
                      ...(formData.image === null && {
                        image: file
                      }),
                      startDate: `${formData.startDate}T00:00:00+00:00`,
                      title: formData.title
                    };
                    AnnouncementUpdate({
                      variables: {
                        id,
                        input: withImageParams
                      }
                    });
                  }}
                />
                <ActionDialog
                  confirmButtonState={announcementDeleteOpts.status}
                  onClose={closeModal}
                  onConfirm={() => announcementDelete({ variables: { id } })}
                  open={params.action === "delete"}
                  title={intl.formatMessage({
                    defaultMessage: "Delete announcement",
                    description: "dialog header"
                  })}
                  confirmButtonLabel="Delete"
                  variant="delete"
                >
                  <DialogContentText>
                    <FormattedMessage
                      defaultMessage="Are you sure you want to delete this announcement?"
                      description="dialog content"
                    />
                  </DialogContentText>
                </ActionDialog>
              </>
            )}
          </TypedAnnouncementUpdate>
        )}
      </TypedAnnouncementDelete>
    );
  } else {
    return (
      <>
        <WindowTitle title={intl.formatMessage(sectionNames.announcements)} />
        <AnnouncementDetailsViewPage
          disabled={false}
          data={maybe(() => data.announcement)}
          onBack={() => navigate(announcementListUrl())}
        />
      </>
    );
  }
};

export default AnnouncementDetails;
